import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, Space, Tooltip } from 'antd'
import {
  ArrowLeftOutlined,
  ExpandOutlined,
  PrinterOutlined,
} from '@ant-design/icons'
import { models } from 'powerbi-client'
import type { Report } from 'powerbi-client'
import { PowerBIEmbed } from 'powerbi-client-react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import PageLoading from '../components/PageLoading'
import { fetchPowerBIEmbed, trackPowerBIUsage } from '../api/dashboard'
import { fetchWorkspaceByTitle } from '../features/practice-agent/api'
import { useToast } from '../contexts/ToastContext'
import { useAuthorization } from '../contexts/AuthorizationContext'
import { queryKeys } from '../api/queryKeys'
import { resolveInitDetailRequest } from '../lib/initDetail'
import { useAuthSession } from '../lib/session'
import {
  useDashboardDetailInit,
  usePowerBIEmbed,
} from '../hooks/useDashboard'
import { ApiRequestError } from '../lib/httpClient'
import type { PowerBIUsageEvent } from '../types/dashboard'
import { dashboardBranding, getFeatureThemeClassName, getFeatureThemeStyle } from '../lib/productBranding'

type ReportViewPageProps = {
  source?: 'practice' | 'advisory'
}

export default function ReportViewPage({ source = 'practice' }: ReportViewPageProps) {
  const session = useAuthSession()
  const { clientId } = useAuthorization()
  const { reportId = '' } = useParams<{ reportId: string }>()
  const initDetailRequest = resolveInitDetailRequest(session)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const reportRef = useRef<Report | null>(null)
  const refreshTimerRef = useRef<number | null>(null)
  const usageFlushTimerRef = useRef<number | null>(null)
  const usageEventsRef = useRef<PowerBIUsageEvent[]>([])
  const sendingUsageRef = useRef(false)
  const embedConfigRef = useRef<Awaited<ReturnType<typeof fetchPowerBIEmbed>> | null>(null)

  const { addToast } = useToast()
  const [isPracticeAgentLoading, setIsPracticeAgentLoading] = useState(false)
  const [isReportRendered, setIsReportRendered] = useState(false)

  const { data: detailInit, isLoading: isDetailInitLoading } = useDashboardDetailInit(initDetailRequest)
  const canAccessReport = detailInit?.reports.some((report) => report.reportId === reportId) ?? false
  const embedEnabled = !isDetailInitLoading && canAccessReport
  const dashboardName = useMemo(
    () => detailInit?.reports.find((report) => report.reportId === reportId)?.name ?? '',
    [detailInit, reportId]
  )
  const reportTypeId = detailInit?.reports.find((report) => report.reportId === reportId)?.typeId ?? 1
  const branding = dashboardBranding[reportTypeId]
  const productLabel = branding.copy.productName
  const itemSingular = branding.copy.itemSingular
  const featureThemeStyle = getFeatureThemeStyle(branding.theme)
  const reportListPath = source === 'advisory' ? '/advisory-dashboards' : '/report'

  const { data: embedConfig, isLoading: isEmbedLoading, isError: isEmbedError } = usePowerBIEmbed(reportId, embedEnabled)

  const embedSettings = useMemo(() => ({
    panes: {
      bookmarks: { visible: false },
      selection: { visible: true },
      filters: { expanded: true, visible: false },
    },
    filterPaneEnabled: false,
    navContentPaneEnabled: false,
    personalBookmarksEnabled: true,
  }), [])

  const powerBIEmbedConfig = useMemo(() => {
    if (!embedConfig) return null

    return {
      type: 'report' as const,
      id: embedConfig.reportId,
      embedUrl: embedConfig.embedURL,
      accessToken: embedConfig.token,
      pageName: embedConfig.pageName || undefined,
      tokenType: models.TokenType.Embed,
      permissions: models.Permissions.All,
      settings: embedSettings,
    }
  }, [embedConfig, embedSettings])

  const handlePracticeAgent = useCallback((searchTerm: string) => {
    void (async () => {
      setIsPracticeAgentLoading(true)
      try {
        const result = await fetchWorkspaceByTitle(clientId, searchTerm)
        if (!result) {
          addToast('No matching report found', 'error')
          return
        }
        if (result.type === 'group') {
          navigate(`/reports/workspace/group/${result.id}`)
        } else {
          navigate(`/reports/workspace/${result.id}`)
        }
      } finally {
        setIsPracticeAgentLoading(false)
      }
    })()
  }, [clientId, navigate, addToast, setIsPracticeAgentLoading])

  const handleEmbedRef = useCallback((embeddedComponent: unknown) => {
    const report = embeddedComponent as Report
    reportRef.current = report

    report.on('buttonClicked', (event) => {
      const d = event.detail as Record<string, unknown>
      const rawTitle = typeof d?.['title'] === 'string' ? (d['title'] as string) : ''
      const normalized = rawTitle.toLowerCase().replace(/[^a-z0-9]/g, '')
      const searchTerm = normalized.replace('practiceagent', '')

      if (rawTitle.toLowerCase().trim().includes('practice_agent')) {
        handlePracticeAgent(searchTerm)
      }
    })
  }, [handlePracticeAgent])

  useEffect(() => {
    embedConfigRef.current = embedConfig ?? null
  }, [embedConfig])

  useEffect(() => {
    setIsReportRendered(false)
  }, [powerBIEmbedConfig])

  useEffect(() => {
    if (!embedConfig) return

    console.debug('[ReportViewPage] Power BI embed API response', {
      reportId,
      embedEnabled,
      embedConfig,
    })
  }, [embedConfig, embedEnabled, reportId])

  useEffect(() => {
    if (!powerBIEmbedConfig) return

    console.debug('[ReportViewPage] Power BI embed config', powerBIEmbedConfig)
  }, [powerBIEmbedConfig])

  const flushUsageEvents = useCallback(async () => {
    if (sendingUsageRef.current || usageEventsRef.current.length === 0) return

    const events = usageEventsRef.current
    sendingUsageRef.current = true

    try {
      await trackPowerBIUsage(events)
      if (usageEventsRef.current === events) {
        usageEventsRef.current = []
      } else {
        usageEventsRef.current = usageEventsRef.current.filter((event) => !events.includes(event))
      }
    } catch {
      // Keep buffered events for a later retry.
    } finally {
      sendingUsageRef.current = false
    }
  }, [])

  const scheduleRefresh = useCallback((expiration: string) => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }

    const expiryMs = new Date(expiration).getTime()
    if (Number.isNaN(expiryMs)) return

    const refreshIn = Math.max(expiryMs - Date.now() - 5 * 60 * 1000, 0)
    refreshTimerRef.current = window.setTimeout(() => {
      void (async () => {
        for (let attempt = 0; attempt <= 3; attempt += 1) {
          try {
            const nextConfig = await queryClient.fetchQuery({
              queryKey: queryKeys.dashboard.embed(reportId),
              queryFn: () => fetchPowerBIEmbed(reportId),
              staleTime: 0,
            })

            embedConfigRef.current = nextConfig
            await reportRef.current?.setAccessToken(nextConfig.token)
            scheduleRefresh(nextConfig.expiration)
            return
          } catch (error) {
            const status = error instanceof ApiRequestError ? error.status : 0
            const retryableStatuses = [0, 500, 502, 503, 504]
            if (!retryableStatuses.includes(status) || attempt >= 3) return

            const delayMs = Math.min(10_000 * 2 ** attempt, 60_000)
            await new Promise((resolve) => window.setTimeout(resolve, delayMs))
          }
        }
      })()
    }, refreshIn)
  }, [queryClient, reportId])

  useEffect(() => {
    usageFlushTimerRef.current = window.setInterval(() => {
      void flushUsageEvents()
    }, 300_000)

    const handleBeforeUnload = () => {
      void flushUsageEvents()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current)
      if (usageFlushTimerRef.current) window.clearInterval(usageFlushTimerRef.current)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      void flushUsageEvents()
    }
  }, [flushUsageEvents])

  const handleReportLoaded = useCallback(() => {
    console.debug('[ReportViewPage] Power BI report loaded', {
      reportId,
      expiration: embedConfigRef.current?.expiration,
      pageName: embedConfigRef.current?.pageName,
    })
    if (embedConfigRef.current?.expiration) {
      scheduleRefresh(embedConfigRef.current.expiration)
    }
  }, [reportId, scheduleRefresh])

  const handleReportRendered = useCallback(() => {
    setIsReportRendered(true)
    console.debug('[ReportViewPage] Power BI report rendered', {
      reportId,
      pageName: embedConfigRef.current?.pageName,
    })
  }, [reportId])

  const handlePageChanged = useCallback((event?: { detail?: { newPage?: { displayName?: string } } }) => {
    const pageName = event?.detail?.newPage?.displayName
    const currentEmbedConfig = embedConfigRef.current
    if (!currentEmbedConfig?.reportId || !pageName) return

    console.debug('[ReportViewPage] Power BI page changed', {
      reportId,
      dashboardId: currentEmbedConfig.reportId,
      pageName,
    })

    usageEventsRef.current.push({
      EventName: 'pageChanged',
      PageName: pageName,
      ReportId: reportId,
      DashboardId: currentEmbedConfig.reportId,
    })

    if (usageEventsRef.current.length >= 500) {
      void flushUsageEvents()
    }
  }, [flushUsageEvents, reportId])

  const handleFullscreen = useCallback(() => {
    const report = reportRef.current as (Report & {
      iframe?: HTMLIFrameElement & {
        requestFullScreen?: () => void
        webkitRequestFullScreen?: () => void
        webkitEnterFullscreen?: () => void
      }
    }) | null

    const iframe = report?.iframe

    console.debug('[ReportViewPage] Fullscreen requested', {
      reportId,
      hasReport: Boolean(report),
      hasIframe: Boolean(iframe),
    })

    if (!iframe) {
      report?.fullscreen()
      return
    }

    if (typeof iframe.requestFullscreen === 'function') {
      void iframe.requestFullscreen()
      return
    }

    if (typeof iframe.requestFullScreen === 'function') {
      iframe.requestFullScreen()
      return
    }

    if (typeof iframe.webkitRequestFullscreen === 'function') {
      iframe.webkitRequestFullscreen()
      return
    }

    if (typeof iframe.webkitRequestFullScreen === 'function') {
      iframe.webkitRequestFullScreen()
      return
    }

    if (typeof iframe.mozRequestFullScreen === 'function') {
      iframe.mozRequestFullScreen()
      return
    }

    if (typeof iframe.msRequestFullscreen === 'function') {
      iframe.msRequestFullscreen()
      return
    }

    if (typeof iframe.webkitEnterFullscreen === 'function') {
      iframe.webkitEnterFullscreen()
      return
    }

    report?.fullscreen()
  }, [reportId])

  const showReportLoader = canAccessReport && !isEmbedError && (isEmbedLoading || (powerBIEmbedConfig && !isReportRendered))

  return (
    <div className={`feature-shell ${getFeatureThemeClassName(branding.featureKey)}`} style={featureThemeStyle}>
      <div
        className="feature-shell__glow"
        aria-hidden
      />

      <PageLoading show={isPracticeAgentLoading} theme={branding.theme} />

      {/* Header */}
      <div className="feature-shell__header px-6 pt-5 pb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(reportListPath)}
              aria-label={`Back to ${productLabel}`}
            />
            <div>
              <h1 className="font-display text-2xl font-bold text-gray-900 tracking-tight">
                {dashboardName || 'Loading...'}
              </h1>
            </div>
          </div>
          <Space>
            <Tooltip title="Fullscreen">
              <Button
                icon={<ExpandOutlined />}
                onClick={() => {
                  handleFullscreen()
                }}
              />
            </Tooltip>
            <Tooltip title="Print">
              <Button
                icon={<PrinterOutlined />}
                onClick={() => {
                  void reportRef.current?.print()
                }}
              />
            </Tooltip>
          </Space>
        </div>
        <div className="feature-shell__accent mt-3 h-1.5 w-16 rounded-full" aria-hidden />
      </div>

      {/* Power BI Embed Area */}
      <div className="p-6">
        {!isDetailInitLoading && detailInit && !canAccessReport && (
          <Card className="text-center py-12">
            <p className="text-lg text-gray-600 mb-2">Your {itemSingular} is not ready yet.</p>
            <p className="text-sm text-gray-400">Please check back in 24 hours.</p>
          </Card>
        )}

        {isEmbedError && canAccessReport && (
          <Card className="text-center py-12">
            <p className="text-lg text-gray-600 mb-2">Your {itemSingular} is not ready yet.</p>
            <p className="text-sm text-gray-400">Please check back in 24 hours.</p>
          </Card>
        )}

        {(showReportLoader || (powerBIEmbedConfig && !isEmbedError && canAccessReport)) && (
          <div className="report-di-loader-shell">
            {showReportLoader && (
              <div className="report-di-loader" aria-live="polite" aria-busy="true">
                <img
                  src="/assets/di-loader.gif"
                  alt="Dashboard Insights loading"
                  className="report-di-loader__image"
                />
              </div>
            )}

            {powerBIEmbedConfig && !isEmbedError && canAccessReport && (
              <div className={isReportRendered ? '' : 'report-di-loader-shell__embed--hidden'}>
                <PowerBIEmbed
                  embedConfig={powerBIEmbedConfig}
                  cssClassName="pbi-embed-container"
                  getEmbeddedComponent={handleEmbedRef}
                  eventHandlers={
                    new Map([
                      ['loaded', handleReportLoaded],
                      ['rendered', handleReportRendered],
                      ['pageChanged', handlePageChanged],
                    ])
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
